import classNames from 'classnames';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';

interface FundData {
  is_fallback: boolean;
  title: string;
  description?: string;
}

interface FundProps {
  fund: FundData;
  isSelected: boolean;
  isHighlighted?: boolean;
  onClick: (fund: FundData) => void;
}

interface StyledProps {
  $isSelected?: boolean;
  $isHighlighted?: boolean;
}

const Fund = ({ fund, isSelected, isHighlighted, onClick }: FundProps) => (
  <Box
    display="inline-block"
    onClick={() => onClick(fund)}
    className="grid__item one-third palm--one-whole"
  >
    <FundWrapper $isHighlighted={isHighlighted} $isSelected={isSelected}>
      <Box alignItems="center" justifyContent="space-between">
        <FundTitle
          $isSelected={isSelected}
          $isHighlighted={isHighlighted}
          data-testid={fund.title}
        >
          {fund.is_fallback
            ? __('Type in your own designation...')
            : fund.title}
        </FundTitle>
        <Checkbox
          display="flex"
          alignItems="center"
          justifyContent="center"
          className={classNames({
            'custom-border-color-button-primary custom-border-color-login-button':
              isSelected,
          })}
        >
          {isSelected && (
            <CheckIcon className="icon-check" data-testid="checkbox-selected" />
          )}
        </Checkbox>
      </Box>
      {isHighlighted && (
        <Dotdotdot clamp={4}>
          <Box
            display="block"
            font="metadata-regular-dove"
            mt={3}
            color={isSelected ? 'white' : undefined}
          >
            {fund.description}
          </Box>
        </Dotdotdot>
      )}
    </FundWrapper>
  </Box>
);

export default Fund;

const FundWrapper = styled(Box).attrs<StyledProps>(({ $isSelected }) => ({
  bg: $isSelected ? 'primary' : 'smoke',
  display: 'block',
  font: 'body-semibold-asphalt',
}))`
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 14px;
  padding: 11px 18px;

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      box-shadow: 2px 4px 5px rgba(0, 0, 0, 0.15);
    `}

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      padding: 14px 18px;
      height: 118px;
    `}
`;

const FundTitle = styled(Box).attrs<StyledProps>(({ $isSelected }) => ({
  font: 'body-semibold-asphalt',
  color: $isSelected ? 'white' : undefined,
}))`
  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      letter-spacing: 0.3px;
      font-size: 15px;
    `}
`;

const Checkbox = styled(Box)<StyledProps>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: solid 1px
    ${({ $isSelected }) => ($isSelected ? 'inherit' : '#d7d7d7')};
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckIcon = styled.i`
  margin-top: 3px;
  font-size: 12px;
  color: var(--color-button-primary);
`;
